<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('update:show', false)">
    <div class="base-rate-modal">
      <h4 class="modal-title">⚡ Ставка направления</h4>
      <p v-if="loading" class="modal-tip">Поиск ставки...</p>
      <template v-else>
        <p class="modal-tip">
          {{ mode === 'edit' ? 'Редактирование существующей ставки.' : 'Ставка не найдена — создание новой.' }}
        </p>
        <label class="modal-field">
          <span class="modal-label">Откуда</span>
          <input v-model="form.from_city" type="text">
        </label>
        <label class="modal-field">
          <span class="modal-label">Куда</span>
          <input v-model="form.to_city" type="text">
        </label>
        <div class="modal-row">
          <label class="modal-field">
            <span class="modal-label">Без НДС</span>
            <input v-model.number="form.cost_without_nds" type="number" min="1">
          </label>
          <label class="modal-field">
            <span class="modal-label">С НДС</span>
            <input v-model.number="form.cost_with_nds" type="number" min="1">
          </label>
        </div>
        <p v-if="validationError" class="modal-error">{{ validationError }}</p>
        <div class="modal-actions">
          <button class="operation-btn operation-manual" :disabled="saving || Boolean(validationError)" @click="onSave">
            {{ mode === 'edit' ? 'Обновить' : 'Создать' }}
          </button>
          <button class="operation-btn" :disabled="saving" @click="$emit('update:show', false)">Закрыть</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { createToaster } from '@meforma/vue-toaster';
import { lookupBaseRate, createBaseRate, updateBaseRate } from '@/backend/baseRatesApi';

const toaster = createToaster();

export default {
  name: 'BaseRateInlineModal',
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: () => ({}) },
  },
  emits: ['update:show', 'saved'],
  data() {
    return {
      loading: false,
      saving: false,
      mode: 'create',
      existingId: null,
      version: 1,
      form: {
        from_city: '',
        to_city: '',
        cost_without_nds: null,
        cost_with_nds: null,
      },
    };
  },
  computed: {
    routeCities() {
      const route = this.order?.order?.route || {};
      return {
        from_city: route.departure?.city_name || '',
        to_city: route.destination?.city_name || '',
      };
    },
    validationError() {
      if (!this.form.from_city?.trim()) return 'Укажите точку отправления';
      if (!this.form.to_city?.trim()) return 'Укажите точку назначения';
      if (!this.form.cost_without_nds || this.form.cost_without_nds <= 0) return 'Цена без НДС должна быть положительной';
      if (!this.form.cost_with_nds || this.form.cost_with_nds <= 0) return 'Цена с НДС должна быть положительной';
      if (this.form.cost_without_nds > this.form.cost_with_nds) return 'Цена без НДС не может превышать цену с НДС';
      return '';
    },
  },
  watch: {
    show(visible) {
      if (visible) this.onOpen();
    },
  },
  methods: {
    async onOpen() {
      const { from_city, to_city } = this.routeCities;
      this.form.from_city = from_city;
      this.form.to_city = to_city;
      this.loading = true;
      this.mode = 'create';
      this.existingId = null;
      this.version = 1;
      this.form.cost_without_nds = null;
      this.form.cost_with_nds = null;
      try {
        const rate = await lookupBaseRate({ from_city, to_city });
        this.mode = 'edit';
        this.existingId = rate.id;
        this.version = rate.version || 1;
        this.form.from_city = rate.from_city || from_city;
        this.form.to_city = rate.to_city || to_city;
        this.form.cost_without_nds = rate.cost_without_nds;
        this.form.cost_with_nds = rate.cost_with_nds;
      } catch (error) {
        if (error?.response?.status !== 404) {
          const detail = error?.response?.data?.detail;
          toaster.error(typeof detail === 'string' ? detail : 'Ошибка поиска ставки');
        }
      } finally {
        this.loading = false;
      }
    },
    async onSave() {
      if (this.validationError) return;
      this.saving = true;
      const payload = {
        from_city: this.form.from_city.trim(),
        to_city: this.form.to_city.trim(),
        cost_without_nds: Number(this.form.cost_without_nds),
        cost_with_nds: Number(this.form.cost_with_nds),
      };
      try {
        let result;
        if (this.mode === 'edit' && this.existingId) {
          payload.version = this.version;
          result = await updateBaseRate(this.existingId, payload);
          toaster.success('Ставка обновлена');
        } else {
          result = await createBaseRate(payload);
          toaster.success('Ставка создана');
        }
        this.$emit('saved', result);
        this.$emit('update:show', false);
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || 'Ошибка сохранения';
        toaster.error(message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.base-rate-modal {
  width: 380px;
  max-width: 90vw;
  padding: 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.modal-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.modal-tip {
  margin: 0 0 12px;
  font-size: 13px;
  color: #666;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.modal-row {
  display: flex;
  gap: 12px;
}

.modal-row .modal-field {
  flex: 1;
}

.modal-label {
  font-size: 13px;
  color: #555;
  font-weight: 600;
}

.base-rate-modal input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
}

.modal-error {
  margin: 0 0 10px;
  font-size: 13px;
  color: #b83235;
  font-weight: 600;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.operation-btn {
  padding: 6px 10px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.operation-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.operation-manual {
  background: #fff3cd;
  border-color: #ffe08a;
  color: #7a5b00;
}
</style>
